import { useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, IconButton, Paper, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import ScheduleIcon from '@mui/icons-material/Schedule'
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import type { SvgIconComponent } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import WizardTextField from './WizardTextField'
import { formatClock, MAX_STIMULUS_MINUTES, stimulusTooLong } from './basicStudy'
import { youTubeId, youTubeThumb } from '@/utils/youtube'
import type { StimulusDraft } from './types'

/** The material types a FaceWise study can show. Everything the product has,
 *  minus the queue: one of these, once. */
const PICKABLE = ['video', 'image', 'text', 'url'] as const
type PickableType = (typeof PICKABLE)[number]

const TYPE_ICON: Record<PickableType, SvgIconComponent> = {
  video: VideocamOutlinedIcon,
  image: ImageOutlinedIcon,
  text: TitleOutlinedIcon,
  url: LinkOutlinedIcon,
}

type BasicStimulusPickerProps = {
  stimulus: StimulusDraft | undefined
  /** A pasted link whose length we are still reading. */
  checking: boolean
  /** Why the last pick was refused (too long), or null. */
  rejected: string | null
  onPickFile: (file: File, type: 'video' | 'image') => void
  onAdd: (type: 'text' | 'url') => void
  onUpdate: (patch: Partial<StimulusDraft>) => void
  onRemove: () => void
}

/**
 * The one material a FaceWise study shows. Picking replaces rather than
 * queues, and a video is measured before it is accepted — the customer finds
 * out that their fifteen-minute film is too long here, not once respondents
 * are already being turned away.
 */
export default function BasicStimulusPicker({
  stimulus,
  checking,
  rejected,
  onPickFile,
  onAdd,
  onUpdate,
  onRemove,
}: BasicStimulusPickerProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const pendingTypeRef = useRef<'video' | 'image'>('video')

  // Local object-URL preview for a file that has not been uploaded yet.
  const [preview, setPreview] = useState<string | null>(null)
  useEffect(() => {
    const file = stimulus?.file
    if (!file) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [stimulus?.file])

  const pick = (type: PickableType) => {
    if (type === 'video' || type === 'image') {
      pendingTypeRef.current = type
      if (fileInputRef.current) {
        fileInputRef.current.accept = type === 'video' ? 'video/*' : 'image/*'
        fileInputRef.current.click()
      }
      return
    }
    onAdd(type)
  }

  const fileInput = (
    <Box
      component="input"
      ref={fileInputRef}
      type="file"
      hidden
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = '' // allow re-picking the same file
        if (file) onPickFile(file, pendingTypeRef.current)
      }}
    />
  )

  const rejection = rejected && (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: c.danger, fontSize: 13, lineHeight: 1.45 }}>
      <ErrorOutlineIcon sx={{ fontSize: 16, mt: '2px' }} />
      <Box component="span">{rejected}</Box>
    </Box>
  )

  if (!stimulus) {
    return (
      <Paper
        sx={{
          border: `1.5px dashed ${c.ink32}`,
          borderRadius: `${theme.affexy.radii.lg / 1.33}px`,
          p: 2.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: '#fff',
        }}
      >
        {fileInput}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: c.coralSoft, display: 'grid', placeItems: 'center' }}>
            <UploadFileOutlinedIcon sx={{ fontSize: 20, color: c.coral }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 14.5 }}>
              {t('wizard.oneScreen.material.addTitle')}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>
              {t('wizard.oneScreen.material.addSub', { minutes: MAX_STIMULUS_MINUTES })}
            </Typography>
          </Box>
        </Box>
        {rejection}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1 }}>
          {PICKABLE.map((type) => {
            const Icon = TYPE_ICON[type]
            return (
              <Box
                key={type}
                component="button"
                type="button"
                onClick={() => pick(type)}
                sx={{
                  bgcolor: c.gray50,
                  border: `1px solid ${c.ink08}`,
                  borderRadius: '12px',
                  p: '14px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': { borderColor: c.coral },
                }}
              >
                <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#fff', border: `1px solid ${c.ink08}`, display: 'grid', placeItems: 'center' }}>
                  <Icon sx={{ fontSize: 15, color: c.coral }} />
                </Box>
                <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 13 }}>
                  {t(`wizard.stimulus.types.${type}.label`)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: c.ink56, lineHeight: 1.3 }}>
                  {t(`wizard.stimulus.types.${type}.sub`)}
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Paper>
    )
  }

  const ytId = stimulus.type === 'url' ? youTubeId(stimulus.url) : null
  const imageSrc = stimulus.type === 'image' ? preview : ytId ? youTubeThumb(ytId) : null
  const videoSrc = stimulus.type === 'video' ? preview : null
  // Basic never offers a prototype (Pro, Z-98); a draft restored with one still needs an icon.
  const Icon = TYPE_ICON[stimulus.type === 'audio' || stimulus.type === 'figma' ? 'video' : stimulus.type]
  const tooLong = stimulusTooLong(stimulus)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        borderRadius: `${theme.affexy.radii.lg / 1.33}px`,
        borderColor: tooLong ? c.danger : 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        bgcolor: '#fff',
      }}
    >
      {fileInput}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        <Box
          sx={{
            width: 132,
            height: 76,
            flex: '0 0 132px',
            borderRadius: '10px',
            overflow: 'hidden',
            display: 'grid',
            placeItems: 'center',
            background:
              imageSrc || videoSrc
                ? '#000'
                : stimulus.type === 'video'
                  ? 'linear-gradient(135deg,#2A2C52,#1B1C38)'
                  : stimulus.type === 'image'
                    ? 'linear-gradient(135deg,#F07E78,#C93A35)'
                    : c.gray100,
          }}
        >
          {imageSrc ? (
            <Box component="img" src={imageSrc} alt={stimulus.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : videoSrc ? (
            <Box
              component="video"
              src={`${videoSrc}#t=0.1`}
              muted
              playsInline
              preload="metadata"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Icon sx={{ fontSize: 22, color: stimulus.type === 'text' || stimulus.type === 'url' ? c.ink56 : '#fff' }} />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {stimulus.type === 'url' ? (
            <WizardTextField
              value={stimulus.url ?? ''}
              onChange={(e) => onUpdate({ url: e.target.value, name: e.target.value, durationSeconds: undefined })}
              placeholder={t('wizard.stimulus.urlPlaceholder')}
            />
          ) : stimulus.type === 'text' ? (
            <WizardTextField
              multiline
              minRows={3}
              maxRows={8}
              value={stimulus.payload ?? ''}
              onChange={(e) => onUpdate({ payload: e.target.value })}
              placeholder={t('wizard.stimulus.textPlaceholder')}
            />
          ) : (
            <Typography
              sx={{
                fontFamily: theme.affexy.fonts.heading,
                fontWeight: 700,
                fontSize: 14,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {stimulus.name}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minHeight: 20 }}>
            {stimulus.meta && (
              <Typography sx={{ fontSize: 12, color: c.ink56, fontVariantNumeric: 'tabular-nums' }}>{stimulus.meta}</Typography>
            )}
            {checking ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: c.ink56, fontSize: 12 }}>
                <CircularProgress size={11} thickness={6} sx={{ color: c.ink48 }} />
                {t('wizard.oneScreen.material.checking')}
              </Box>
            ) : stimulus.durationSeconds != null ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 12,
                  fontWeight: 600,
                  color: tooLong ? c.danger : c.ink56,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <ScheduleIcon sx={{ fontSize: 14 }} />
                {formatClock(stimulus.durationSeconds)}
              </Box>
            ) : null}
          </Box>
        </Box>

        <IconButton
          aria-label={t('wizard.oneScreen.material.remove')}
          onClick={onRemove}
          sx={{ color: c.danger, flex: '0 0 auto' }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {tooLong && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: c.danger, fontSize: 13, lineHeight: 1.45 }}>
          <ErrorOutlineIcon sx={{ fontSize: 16, mt: '2px' }} />
          <Box component="span">{t('wizard.oneScreen.material.tooLong', { minutes: MAX_STIMULUS_MINUTES })}</Box>
        </Box>
      )}
      {rejection}
      <Typography sx={{ fontSize: 12.5, color: c.ink56, lineHeight: 1.45 }}>
        {t('wizard.oneScreen.material.replaceHint')}
      </Typography>
    </Paper>
  )
}
